/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-return-assign */
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import videojs, { VideoJsPlayer } from 'video.js';
import useStores from '@lib/hooks/useStores';
import styled from '@emotion/styled';
import classNames from 'classnames';

import 'video.js/dist/video-js.css';

import { IVideoStore } from '@stores/videoStore';
import { IPlayerAttributes } from '@interfaces/video.interface';

interface IPlayerProps {
  children?: React.ReactNode;
  onPlay?: () => void;
  onPause?: () => void;
}

const Player: React.FC<IPlayerProps & IPlayerAttributes> = observer(
  ({
    children,
    autoplay,
    controls,
    muted = false,
    fluid,
    inactivityTimeout,
    controlBar,
    onPlay,
    onPause,
  }) => {
    const { videoStore } = useStores<{ videoStore: IVideoStore }>();
    const videoNode = useRef<HTMLVideoElement | null>(null);
    const [isReachedEndTime, setReachedEndTime] = useState(false);
    const player = useRef<VideoJsPlayer | null>(null);

    // Initialize
    useEffect(() => {
      if (videoNode.current) {
        videoNode.current.addEventListener('loadedmetadata', () => {
          videoStore.setPlayerData({
            duration: videoNode.current?.duration,
          });
        });

        videoStore.setVideoNode(videoNode.current);
      }
      player.current = videojs(
        videoNode.current!,
        {
          autoplay,
          controls,
          muted,
          fluid,
          inactivityTimeout,
          controlBar: controlBar
            ? { children: controlBar.children }
            : {
                children: [
                  'playToggle',
                  'currentTimeDisplay',
                  'timeDivider',
                  'durationDisplay',
                  'progressControl',
                  // 'liveDisplay',
                  // 'seekToLive',
                  'remainingTimeDisplay',
                  'customControlSpacer',
                  'playbackRateMenuButton',
                  // 'chaptersButton',
                  // 'descriptionsButton',
                  // 'subsCapsButton',
                  'volumePanel',
                  // 'audioTrackButton',
                  // 'pictureInPictureToggle',
                  'fullscreenToggle',
                ],
              },
        },
        () => {
          // console.log('onPlayerReady');
        },
      );

      videoStore.setPlayerRef(player.current);

      videoNode.current?.addEventListener('play', videoNodeEventHandler);
      videoNode.current?.addEventListener('playing', videoNodeEventHandler);
      videoNode.current?.addEventListener('pause', videoNodeEventHandler);
      videoNode.current?.addEventListener('progress', videoNodeEventHandler);
      videoNode.current?.addEventListener('seeking', videoNodeEventHandler);
      videoNode.current?.addEventListener('seeked', videoNodeEventHandler);
      videoNode.current?.addEventListener('ended', videoNodeEventHandler);
      videoNode.current?.addEventListener('timeupdate', videoNodeEventHandler);

      return () => {
        videoNode.current?.removeEventListener('play', videoNodeEventHandler);
        videoNode.current?.removeEventListener('playing', videoNodeEventHandler);
        videoNode.current?.removeEventListener('pause', videoNodeEventHandler);
        videoNode.current?.removeEventListener('progress', videoNodeEventHandler);
        videoNode.current?.removeEventListener('seeking', videoNodeEventHandler);
        videoNode.current?.removeEventListener('seeked', videoNodeEventHandler);
        videoNode.current?.removeEventListener('ended', videoNodeEventHandler);
        videoNode.current?.removeEventListener('timeupdate', videoNodeEventHandler);
        player.current?.dispose();
      };
    }, []);

    // Handler for timeupdate and pause when reached chapter endTime
    useEffect(() => {
      let raf: any;

      if (videoNode.current) {
        const { paused } = videoNode.current;

        const timeupdate = () => {
          if (!paused && videoNode.current) {
            // Play
            const { playerData } = videoStore;
            const { currentTime } = videoNode.current!;

            if (playerData.range) {
              const { endTime } = videoStore.playerData.range!;

              if (currentTime >= endTime) {
                videoNode.current?.pause();
                cancelAnimationFrame(raf);
                setReachedEndTime(true);
              } else {
                if (isReachedEndTime) setReachedEndTime(false);
              }
            }

            if (currentTime !== videoStore.playerData.currentTime) {
              videoStore.setPlayerData({ currentTime });
            }
            raf = requestAnimationFrame(timeupdate);
          } else {
            cancelAnimationFrame(raf);
          }
        };

        if (!paused) {
          // Play
          timeupdate();
        } else {
          // Pause
          cancelAnimationFrame(raf);
        }
      }

      return () => {
        cancelAnimationFrame(raf);
      };
    }, [videoNode.current?.paused]);

    const videoNodeEventHandler = useCallback(
      (e: Event) => {
        const { range } = videoStore.playerData;
        if (videoNode.current) {
          switch (e.type) {
            case 'play':
              videoStore.setPlayerData({ paused: videoNode.current.paused });
              if (onPlay) onPlay();
              break;
            case 'playing':
              videoStore.setPlayerData({ paused: videoNode.current.paused });
              break;
            case 'pause':
              videoStore.setPlayerData({ paused: videoNode.current.paused });
              if (onPause) onPause();
              break;
            case 'progress':
              videoStore.setPlayerData({ paused: videoNode.current.paused });
              break;
            case 'seeking':
              setReachedEndTime(
                range && videoNode.current.currentTime >= range.endTime ? true : false,
              );
              videoStore.setPlayerData({ currentTime: videoNode.current.currentTime });
              break;
            case 'seeked':
              setReachedEndTime(
                range && videoNode.current.currentTime >= range.endTime ? true : false,
              );
              videoStore.setPlayerData({ currentTime: videoNode.current.currentTime });
              break;
            case 'ended':
              videoStore.setPlayerData({ paused: videoNode.current.paused });
              break;
            case 'timeupdate':
              videoStore.setPlayerData({ currentTime: videoNode.current.currentTime });
              break;
            default:
          }
        }
      },
      [videoStore.playerData, videoNode],
    );

    return (
      <PlayerBlock>
        <div className={classNames('screen', { 'is-reached-chapter-end': isReachedEndTime })}>
          <video className="video-js" ref={videoNode}>
            {children}
          </video>
        </div>
      </PlayerBlock>
    );
  },
);

const PlayerBlock = styled.div`
  position: relative;
  overflow: hidden;
  width: auto;
  height: 100%;

  .screen {
    position: relative;
    height: 100%;
    visibility: visible;

    &.is-reached-chapter-end {
      video {
        pointer-events: none;
      }
    }
  }
`;

export default Player;
